import {
  ActionHandler,
  BaseSeqState,
  PickMainState,
  GenerateActionTypes,
  BLC_STATUS_TYPE,
  generatePrefixedActions,
  // MasterMapOptions,
} from "react-lib/apps/HISV3/common/CommonInterface";
import WasmController from "@/react-lib/frameworks/WasmController";

/*================================ SERIALIZERS ==============================*/

import ProxyPatientHasDivisionAdminSerializer from "issara-sdk/types/ProxyPatientHasDivisionAdminSerializer_apps_PRX";
import ProxyPatientSerializer from "issara-sdk/types/ProxyPatientSerializer_apps_PRX";
import ProxyPatientListSerializer from "issara-sdk/types/ProxyPatientListSerializer_apps_PRX";
import ProxyPatientHasDivisionSerializer from "issara-sdk/types/ProxyPatientHasDivisionSerializer_apps_PRX";
import UserProfileSerializer from "issara-sdk/types/UserProfileSerializer_users";
import EmployeeSerializer from "issara-sdk/types/EmployeeSerializer_users";
import PatientSerializer from "issara-sdk/types/PatientSerializer_apps_REG";
import EncounterSerializer from "issara-sdk/types/EncounterSerializer_core";
import DoctorSerializer from "issara-sdk/types/DoctorSerializer_core";
import PatientAppointmentSerializer from "issara-sdk/types/PatientAppointmentSerializer_apps_QUE";
import AppointmentEmployeePositionSerializer from "@/issara-sdk/types/AppointmentEmployeePositionSerializer_apps_APP";
import type { OrderSummaryFilter, OrderSummaryList } from "./PackageOrderSummaryType";

/*================================ SEQUENCE ACTION PARAMS ==============================*/

export const ACTIONS = {
  GET_ORDER_SUMMARY: "GET_ORDER_SUMMARY",
  // --- หน้าจอรายชื่อผู้รับบริการ
  GET_CUSTOMER_LIST: "GET_CUSTOMER_LIST",
  CREATE_CUSTOMER: "CREATE_CUSTOMER",
  UPDATE_CUSTOMER: "UPDATE_CUSTOMER",
  LINK_CUSTOMER: "LINK_CUSTOMER",

  // ----
  GET_CUSTOMER_DETAIL: "GET_CUSTOMER_DETAIL",

  // --- ลงทะเบียนรับบริการ
  CREATE_VISIT: "CREATE_VISIT",
  CREATE_VISIT_APPOINTMENT: "CREATE_VISIT_APPOINTMENT",
  GET_VISIT_LIST: "GET_VISIT_LIST",
  UPDATE_VISIT: "UPDATE_VISIT",
  DELETE_VISIT: "DELETE_VISIT",
  GET_APPOINTMENT_LIST: "GET_APPOINTMENT_LIST",

  GET_PACKAGE_DETAIL: "GET_PACKAGE_DETAIL",

  // --- Print List (Test เดี๋ยวต้องย้ายไปอยู่ CardPrintList ที่เดีัยวกัน)
  PRINT_CONSENT_PDPA: "PRINT_CONSENT_PDPA",
  PRINT_CONSENT_VISIT: "PRINT_CONSENT_VISIT",
  GET_PATIENT_DETAIL: "GET_PATIENT_DETAIL",

  SELECTED_INITIAL_MENU: "SELECTED_INITIAL_MENU",
} as const;

/*================================ CONSTANTS ==============================*/

export const SEQUENCE_NAME = "MeBilling" as const;
export const MAIN_CARD_NAME = "CardMeBillingMain" as const;
// ....

export const DEFAULT_LIST_LIMIT = 10; // Limit สำหรับ Pagination

export const StateInitial: State = {
  MeBillingSequence: null,
};

export const DataInitial: Data = {};

export const Masters = [
  ["prenameTh", {}],
  ["prenameEn", {}],
  ["gender", {}],
  ["country", {}],
  ["province", {}],
  ["homeType", {}],
  ["division", {}],
  ["cancel_receipt", {}],
  ["packageType", {}],
  ["relative", {}],
  ["district", {}],
  ["city", {}],
] as const;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/*================================ STATE ==============================*/

export type State = {
  selectedPatient: Record<string, any>;
  selectedEncounter: Record<string, any>;
  meBillingActiveTab: number;
  meBillingInitialMenu: string;
  receiptId: number | null;
  PaymentHistorySequence: Record<string, any>;
  MeBillingSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;

    // --- รายการ Common Data Ex. DoctorList, Provider etc.
    common: MeBillingCommonData;

    // list customer
    customerList: CustomerListData;
    // select customer
    selectedCustomer: SelectedCustomerData;
    // list visit
    visitList: VisitListData;
    selectedVisit?: EncounterSerializer;
    // list appointment
    appointmentList: AppointmentListData;
    packageDetail: Record<any, string>[] | null;
    orderSummary: OrderSummaryList;
  }> | null;
};

export type MeBillingSequence = Partial<{
  sequenceIndex: "Start" | "Action" | null;

  common: MeBillingCommonData;

  // list customer
  customerList: CustomerListData;
  // select customer
  selectedCustomer: SelectedCustomerData;
  // list visit
  visitList: VisitListData;
  selectedVisit?: EncounterSerializer;
  orderSummary: OrderSummaryList;
}> | null;

export type MeBillingCommonData = {
  // todo --- users/apis/employee (สำหรับไว้ลงทะเบียน Visit)
  providerList: EmployeeSerializer[];
  doctorList: DoctorSerializer[];
};

export type CustomerListData = {
  items: ProxyPatientHasDivisionAdminSerializer[];
  activePage: number;
  total: number;
};

export type SelectedCustomerData = Partial<{
  patientDetail: PatientSerializer;
  proxyPatient: ProxyPatientSerializer;
  proxyPatientAdmin: ProxyPatientHasDivisionAdminSerializer;
}>;

export type VisitListData = {
  items: EncounterSerializer[];
  activePage: number;
  total: number;
};

export type AppointmentListData = {
  items: PatientAppointmentSerializer[];
  activePage: number;
  total: number;
};

/*================================ GENERIC TYPES ==============================*/

export type AppointmentFinishResponse = {
  encounters: number[];
  items: AppointmentEmployeePositionSerializer[];
};

/*================================ PARAM TYPES ==============================*/

export type GetCustomerListParams = Partial<{
  patient_name_search: string;
  identity_document_number: string;
  phone_no: string;
}>;

export type CreateCustomerParams = PatientSerializer & {
  confirmSave?: boolean;
  create_for_division?: number;
  email?: string;
  line_id?: string;
  display_hn?: string; // --- ไว้แสดงบน Mod
  relative_address_owner_name?: string;
  relative_address_tel_mobile?: string;
};

// todo --- ตอนนี้ Based on ProxyPatientDetail
export type GetCustomerDetailParams = {
  customerId: number;
  proxyPatientId: number; // *** ถ้าใช้ patientDetail ใน Workflow อย่างเดียวให้เอาออกได้เลย
};

/**
 * * NOTE : บังคับให้ type เป็น OPD ทั้งหมด
 */
export type CreateVisitParams = EncounterSerializer;

// ref ตาม EncounterPatientListOptiomized
export type GetVisitListParams = Partial<{
  patient: number;
  patient_hn: string;
  doctor: number;
  from_date: string;
  to_date: string;
}>;

export type GetPatientAppointmentListParams = Partial<{
  patient_id: number;
  hn: string;
  division_id: number;
  exclude_cancel: boolean;
  status: string;
  order_by_app_date: boolean;
  ordering: string | string[];
  include_appointment_package_confirm: boolean;
  exclude_appointment_with_encounter: boolean;
  id__in: string;
}>;

export type GetAppointmentListParams = Partial<{
  patient: number;
  doctor: number;
  division: number;
  exclude_canceled: boolean;
  exclude_finished: boolean;
}>;

export type CreateVisitAppointParams = Partial<{
  items: AppointmentEmployeePositionSerializer[];
  confirmed: boolean;
}>;

/*================================ SEQUENCE ACTION TYPES ==============================*/

export type ActionPayloadMap = {
  [ACTIONS.GET_ORDER_SUMMARY]: {
    divisionId: number;
    filter?: OrderSummaryFilter;
    activePage?: number;
  };
  [ACTIONS.GET_CUSTOMER_LIST]: { filter?: GetCustomerListParams; activePage?: number };
  [ACTIONS.CREATE_CUSTOMER]: {
    customerData: CreateCustomerParams;
    onSuccess?: () => void;
  };
  [ACTIONS.UPDATE_CUSTOMER]: {
    customerId: number;
    customerData: CreateCustomerParams;
    onSuccess?: () => void;
  };
  [ACTIONS.LINK_CUSTOMER]: {
    customerId: number;
    linkedId: number | string;
    onSuccess?: () => void;
  };
  [ACTIONS.GET_CUSTOMER_DETAIL]: GetCustomerDetailParams;

  // --- Visit
  [ACTIONS.CREATE_VISIT]: {
    visitData: CreateVisitParams;
    onSuccess?: () => void;
  };
  [ACTIONS.UPDATE_VISIT]: {
    visitId: number;
    visitData: CreateVisitParams;
    onSuccess?: () => void;
  };
  [ACTIONS.DELETE_VISIT]: {
    username: string;
    password: string;
    note: string;
    onSuccess?: () => void;
  };
  [ACTIONS.GET_VISIT_LIST]: { filter?: GetVisitListParams; activePage?: number };
  [ACTIONS.CREATE_VISIT_APPOINTMENT]: {
    appointmentId: number;
    patient: number;
    providerName?: string;
    onSuccess?: () => void;
  };

  // --- Appointment
  [ACTIONS.GET_APPOINTMENT_LIST]: { filter?: GetPatientAppointmentListParams; activePage?: number };
  [ACTIONS.GET_PACKAGE_DETAIL]: { packageIds: number[]; index: number };
  [ACTIONS.GET_PATIENT_DETAIL]: { patientID: number; onSuccess?: () => void };

  [ACTIONS.SELECTED_INITIAL_MENU]: {
    data?: {
      patient?: ProxyPatientSerializer;
      receipt?: number;
    };
    initialMenu?: string;
  };
};

/*================================ COMMON TYPES ==============================*/

export type PickedState = PickMainState<
  | "buttonLoadCheck"
  | "errorMessage"
  | "successMessage"
  | "loadingStatus"
  | "django"
  | "masterOptions"
  | "userProfile"
  | "selectedDivision"
  | "searchedItemListWithKey"
  | "qrCodePayment"
  | "goToMeGen"
>;

export type Event =
  | { message: "RunSequence"; params: Record<string, unknown> }
  | { message: "GetMasterData"; params: Record<string, unknown> };

export type Data = Partial<{
  division: number;
  device: number;
  masterData?: any;
  userProfile?: UserProfileSerializer & { full_name: string };
}>;

export type Actions = typeof ACTIONS;

export type SequenceName = typeof SEQUENCE_NAME;

export type SeqState = BaseSeqState<SequenceName>;

export type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

export type LoadingStatusKey = keyof PickedState["loadingStatus"];

export type ButtonLoadCheckKey = keyof PickedState["buttonLoadCheck"];

export type ErrorMessageKey = keyof PickedState["errorMessage"];

export type SuccessMessageKey = keyof PickedState["successMessage"];

export type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

export type Controller = WasmController<PickedState & State, Event, Data, {}>;

export type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = any, R = any> = ActionHandler<PickedState & State, Event, Data, P, R>;

/*================================ HELPER SETSTATE ==============================*/

export const setLoadingStatus = (
  controller: Controller,
  key: LoadingStatusKey,
  loading: boolean,
) => {
  controller.setState((prev) => ({
    loadingStatus: {
      ...prev.loadingStatus,
      [key]: loading,
    },
  }));
};

export const setButtonLoadCheck = (
  controller: Controller,
  key: ButtonLoadCheckKey,
  status: BLC_STATUS_TYPE | null,
) => {
  controller.setState((prev) => ({
    buttonLoadCheck: {
      ...prev.buttonLoadCheck,
      [key]: status,
    },
  }));
};

export const setErrorMessage = (controller: Controller, key: ErrorMessageKey, error: any) => {
  controller.setState((prev) => ({
    errorMessage: {
      ...prev.errorMessage,
      [key]: error,
    },
  }));
};

export const setSuccessMessage = (controller: Controller, key: SuccessMessageKey, message: any) => {
  controller.setState((prev) => ({
    successMessage: {
      ...prev.successMessage,
      [key]: message,
    },
  }));
};
